import type { CandidateReport, ReportStage } from '@eduvy/core';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { Icon, type IconName } from '../components/icons';
import { Avatar, Badge, Button, Callout, Card, Loading, LogoMark, ProgressBar, Ring, cx } from '../components/ui';
import { CANDIDATE_STATUS, VERDICT, formatDateTime, scoreText } from '../lib/format';

type Load = { kind: 'loading' } | { kind: 'ready'; report: CandidateReport } | { kind: 'error'; message: string };

const DECISION_LABEL: Record<string, string> = { SHORTLIST: 'Shortlisted', ADVANCE: 'Invited to recruiter interview', SELECT: 'Selected', REJECT: 'Rejected', HOLD: 'On hold' };
const STAGE_ICON: Record<ReportStage['stage'], IconName> = { screening: 'headphones', assessment: 'target', recruiter: 'users' };

/** A candidate's results for a hiring manager without a login (/r/:token). Read-only, no contact details. */
export function ReportPage() {
  const { token } = useParams() as { token: string };
  const [load, setLoad] = useState<Load>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    fetch(`/api/public/report/${encodeURIComponent(token)}`)
      .then(async (r) => {
        const body = (await r.json().catch(() => ({}))) as CandidateReport & { error?: string };
        if (!r.ok) throw new Error(body.error ?? 'This report link is not valid.');
        if (!cancelled) setLoad({ kind: 'ready', report: body });
      })
      .catch((err: unknown) => !cancelled && setLoad({ kind: 'error', message: err instanceof Error ? err.message : 'This report link is not valid.' }));
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (load.kind === 'loading') return <Loading className="min-h-full" />;
  if (load.kind === 'error')
    return (
      <div className="mx-auto max-w-xl px-4 py-16">
        <Callout tone="warning">{load.message}</Callout>
      </div>
    );
  const r = load.report;
  const status = CANDIDATE_STATUS[r.status as keyof typeof CANDIDATE_STATUS] ?? { label: r.status, tone: 'slate' as const };

  return (
    <div className="min-h-full bg-bg">
      <header className="border-b border-border bg-surface/70 print:hidden">
        <div className="mx-auto flex max-w-4xl items-center gap-3 px-4 py-4 sm:px-6">
          <LogoMark className="h-8 w-8" />
          <span className="display text-lg font-semibold text-fg">Eduvy</span>
          <span className="ml-auto text-xs text-muted-fg">Shared report · valid until {formatDateTime(r.expiresAt)}</span>
          <Button size="sm" icon="download" onClick={() => window.print()}>
            Print
          </Button>
        </div>
      </header>
      <main className="mx-auto max-w-4xl space-y-5 px-4 py-8 sm:px-6">
        <div className="flex flex-wrap items-center gap-4">
          <Avatar name={r.candidate.name} size="lg" ring />
          <div className="min-w-0 flex-1">
            <p className="eyebrow">
              {r.job.title} · {r.job.company}
            </p>
            <h1 className="display mt-1 text-[30px] font-medium leading-tight text-fg">{r.candidate.name}</h1>
            <p className="mt-1 text-sm text-muted-fg">{[r.candidate.experience, r.candidate.sector, r.candidate.location ?? r.job.location].filter(Boolean).join(' · ')}</p>
          </div>
          <Badge tone={status.tone} dot>
            {status.label}
          </Badge>
        </div>

        {r.stages.map((s) => (
          <StageCard key={s.stage} s={s} />
        ))}

        <Card title="Decisions" description="By the hiring team, newest first.">
          {r.decisions.length === 0 ? (
            <p className="text-sm text-muted-fg">No decision yet.</p>
          ) : (
            <ol className="relative space-y-4 border-l border-border pl-5 text-sm">
              {r.decisions.map((d, i) => (
                <li key={i} className="relative">
                  <span className="absolute -left-[26px] top-1 h-2.5 w-2.5 rounded-full bg-accent ring-4 ring-surface" aria-hidden />
                  <span className="font-semibold text-fg">{DECISION_LABEL[d.kind] ?? d.kind}</span>
                  <span className="ml-2 text-xs text-muted-fg">
                    {d.by} · {formatDateTime(d.at)}
                  </span>
                  {d.reason && <p className="mt-1 text-fg-2">{d.reason}</p>}
                </li>
              ))}
            </ol>
          )}
        </Card>
        <p className="text-xs text-faint">Generated {formatDateTime(r.generatedAt)}. The AI's scores inform; the hiring team decides. Contact details are not part of this report.</p>
      </main>
    </div>
  );
}

function StageCard({ s }: { s: ReportStage }) {
  const started = s.status !== 'Not started';
  return (
    <Card
      title={
        <span className="inline-flex items-center gap-2">
          <Icon name={STAGE_ICON[s.stage]} className="h-4 w-4 text-accent" />
          {s.label}
        </span>
      }
      actions={<span className={cx('text-xs', started ? 'text-muted-fg' : 'text-faint')}>{s.status.startsWith('Booked for ') ? `Booked for ${formatDateTime(s.status.slice('Booked for '.length))}` : s.status}</span>}
      className={cx(!started && 'opacity-70')}
    >
      {s.score === null ? (
        <p className="text-sm text-muted-fg">{started ? 'No score yet.' : 'This round has not happened.'}</p>
      ) : (
        <div className="space-y-6">
          <div className="flex items-center gap-5">
            <Ring value={s.score} size={96} stroke={8} tone="auto" label={`Score ${s.score} of 100`}>
              <span className={cx('display text-[30px] font-medium leading-none tabular', scoreText(s.score))}>{s.score}</span>
              <span className="mt-1 font-mono text-[9px] uppercase tracking-wider text-muted-fg">of 100</span>
            </Ring>
            <div className="min-w-0 flex-1">
              {s.verdict && <Badge tone={VERDICT[s.verdict].tone}>{VERDICT[s.verdict].label}</Badge>}
              {s.recommendation && (
                <p className="mt-2 text-sm">
                  Recommendation <strong className="font-semibold">{s.recommendation}</strong>
                </p>
              )}
              {s.summary && <p className="mt-2 text-sm leading-relaxed text-fg-2">{s.summary}</p>}
            </div>
          </div>

          {s.competencies.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-fg">Competencies</h3>
              {s.competencies.map((c) => (
                <div key={c.label}>
                  <div className="mb-1.5 flex items-baseline justify-between gap-2 text-sm">
                    <span className="font-medium text-fg">
                      {c.label} <span className="font-mono text-[11px] text-faint">× {c.weight}</span>
                    </span>
                    <span className="font-mono text-xs text-muted-fg">{c.score !== null ? `${c.score} / 5` : 'not assessed'}</span>
                  </div>
                  <ProgressBar value={c.score ?? 0} max={5} size="sm" />
                  {c.rationale && <p className="mt-1.5 text-xs leading-relaxed text-muted-fg">{c.rationale}</p>}
                  {c.evidence.length > 0 && (
                    <ul className="mt-1 space-y-0.5 text-xs italic text-fg-2">
                      {c.evidence.map((q, i) => (
                        <li key={i}>“{q}”</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}

          {s.tasks.length > 0 && (
            <div>
              <h3 className="mb-2 text-sm font-semibold text-fg">Tasks</h3>
              <ol className="space-y-3">
                {s.tasks.map((t, i) => (
                  <li key={i}>
                    <div className="flex items-baseline justify-between gap-2 text-sm">
                      <span className="font-medium text-fg">
                        {i + 1}. {t.title} <span className="font-mono text-[11px] text-faint">{t.kind}</span>
                      </span>
                      <span className="font-mono text-xs text-muted-fg">{t.score !== null ? `${t.score} / 5` : 'not scored'}</span>
                    </div>
                    {t.score !== null && <ProgressBar value={t.score} max={5} size="sm" className="mt-1.5" />}
                    {t.rationale && <p className="mt-1.5 text-xs leading-relaxed text-muted-fg">{t.rationale}</p>}
                  </li>
                ))}
              </ol>
            </div>
          )}

          {s.facts.length > 0 && (
            <div>
              <h3 className="mb-2 text-sm font-semibold text-fg">Facts established</h3>
              <table className="w-full text-sm">
                <tbody className="divide-y divide-border">
                  {s.facts.map((f) => (
                    <tr key={f.label}>
                      <td className="w-2/5 py-2 pr-2 align-top text-muted-fg">{f.label}</td>
                      <td className="py-2 align-top text-fg">{f.value}</td>
                      <td className="py-2 pl-2 text-right align-top">
                        {f.rule === 'passed' && (
                          <Badge tone="green" size="xs">
                            OK
                          </Badge>
                        )}
                        {f.rule === 'failed' && (
                          <Badge tone="red" size="xs">
                            {f.message ?? 'Failed'}
                          </Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-3">
            <Notes title="Strengths" items={s.strengths} dot="bg-success" />
            <Notes title="Concerns" items={s.concerns} dot="bg-warning" />
            <Notes title="Red flags" items={s.redFlags} dot="bg-destructive" />
          </div>
        </div>
      )}
    </Card>
  );
}

function Notes({ title, items, dot }: { title: string; items: string[]; dot: string }) {
  return (
    <div>
      <h4 className="mb-1.5 text-sm font-semibold text-fg">{title}</h4>
      {items.length === 0 ? (
        <p className="text-xs text-faint">None noted.</p>
      ) : (
        <ul className="space-y-1 text-sm text-fg-2">
          {items.map((x) => (
            <li key={x} className="flex gap-2">
              <span className={cx('mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full', dot)} />
              <span className="leading-relaxed">{x}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
